import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Grid, Hidden, Link, Button, useMediaQuery, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@material-ui/icons/Person';

// UTILITY
import { externalLink } from '@/utility/analytics';

// CONSTANTS
import { ROUTE_ROOT } from '@/constants/routes';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { AppActions } from '@/store/actions';

// HOC
import Guard from '@/hoc/Guard';

// COMPONENTS
import Logo from '@/components/Logo';
import LogoPartner from '@/components/LogoPartner';
import Stepper from '@/components/Stepper';
import RouterLink from '@/components/Link';
import Header from '@/components/Headers/Header';

// STYLES
import { RoundButton } from '@/components/Headers/MyBundleHeader/styles';
import { StyledToolbar, StyledAppBar } from './styles';

const HeaderWizard = props => {
  const {
    type,
    hideSteppers,
    questionStepperProps,
    channelStepperProps,
    showPrev,
    showNext,
    onPrevPress,
    onNextPress,
    prevBtnProps,
    nextBtnProps,
    showSubmitBtn,
    onSubmit,
    submitBtnProps,
    showProfileBtn,
    questionsCount,
    channelQuestionsCount,
    partner,
    pageName,
    loading,
    location,
  } = props;
  const [t] = useTranslation();
  const { isInAppView, isAuth } = useSelector(({ app, auth }) => ({
    isInAppView: app.isInAppView,
    isAuth: auth.isAuth,
  }));
  const [manageProfDrawerState] = useActions([
    state => AppActions.manageProfDrawerState.action({ state }),
  ]);
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));

  const isFmb = type === 'fmb';
  const isChannels = type === 'channels';
  const isFmbChannelsMobile = (isFmb || isChannels) && isMobile;
  const partnerSettings = partner.settings || {};

  if (isInAppView) return null;

  if (partner && partnerSettings.use_custom_header) {
    return <Header />;
  }

  const handleExternalLink = ({ target }) => {
    const href = target.href || target.parentNode?.href;
    const properties = {
      location: 'wizard header',
      href,
      partner: partner.partner_slug,
      partner_id: partner.partner_id,
    };

    externalLink(properties);
  };

  const handlePrevClick = event => {
    const { disabled } = prevBtnProps;

    event.preventDefault();

    if (!disabled) {
      onPrevPress();
    }
  };

  const handleNextClick = event => {
    const { disabled } = nextBtnProps;

    event.preventDefault();

    if (!disabled) {
      onNextPress();
    }
  };

  const handleSubmitClick = event => {
    const { disabled } = showSubmitBtn;

    event.preventDefault();

    if (!disabled) {
      onSubmit();
    }
  };

  return (
    <StyledAppBar position="static" className={clsx(pageName, type)}>
      <StyledToolbar className="toolbar">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={4} sm={5} lg={4} className="backtohome">
            {partner.home_link ? (
              <>
                <Hidden smDown mdDown={isFmbChannelsMobile}>
                  <Link href={partner.home_link} target="_blank" onClick={handleExternalLink}>
                    <i className="icon-arrow-left m-r-10" />
                    {partner?.text_overwrites?.back_to_site_link ||
                      t('buttons.backToHomePartner', { partner_name: partner.partner_name })}
                  </Link>
                </Hidden>
                <Hidden mdUp mdDown={isFmbChannelsMobile}>
                  <Link
                    className="toolbar__link noselect"
                    color={partner.partner_slug ? 'primary' : 'error'}
                    href={partner.home_link}
                    target="_blank"
                    onClick={handleExternalLink}
                  >
                    {t('buttons.exit')}
                  </Link>
                </Hidden>
              </>
            ) : (
              <>
                <Hidden smDown mdDown={isFmbChannelsMobile}>
                  <Link to={ROUTE_ROOT} component={RouterLink} location={location}>
                    <i className="icon-arrow-left m-r-10" />
                    {t('buttons.backToHomePage')}
                  </Link>
                </Hidden>
                <Hidden mdUp mdDown={isFmbChannelsMobile}>
                  <Link
                    className="toolbar__link noselect"
                    color="error"
                    to={ROUTE_ROOT}
                    component={RouterLink}
                    location={location}
                  >
                    {t('buttons.exit')}
                  </Link>
                </Hidden>
              </>
            )}
          </Grid>
          <Hidden xsDown={!isFmbChannelsMobile}>
            <Grid
              className="toolbar__logos"
              item
              xs={isFmbChannelsMobile ? 12 : 2}
              lg={4}
              container
              justifyContent="space-around"
            >
              {!loading && (
                <>
                  {partner.logo ? (
                    <LogoPartner location={location} externalLinkClick={handleExternalLink} />
                  ) : (
                    <Logo location={location} />
                  )}
                </>
              )}
            </Grid>
          </Hidden>
          <Grid item xs={8} sm={5} lg={4} className="right-section">
            {!hideSteppers && (
              <Hidden smDown mdDown={isFmbChannelsMobile}>
                <Grid container spacing={4} justifyContent="flex-end">
                  {!!questionsCount && (
                    <Grid item>
                      <Stepper
                        name={t('headerWizard.questions')}
                        count={questionsCount}
                        {...questionStepperProps}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    <Stepper
                      name={t('headerWizard.channels')}
                      count={channelQuestionsCount}
                      {...channelStepperProps}
                    />
                  </Grid>
                </Grid>
              </Hidden>
            )}
            {type !== 'results' && (
              <Hidden mdUp mdDown={isFmbChannelsMobile}>
                <Grid container spacing={4} justifyContent="flex-end" alignItems="center">
                  {showPrev && (
                    <Grid item>
                      <Link
                        href="#"
                        {...prevBtnProps}
                        className="toolbar__link noselect prevlink"
                        color="inherit"
                        onClick={handlePrevClick}
                      >
                        {t('buttons.previous')}
                      </Link>
                    </Grid>
                  )}
                  {showNext && (
                    <Grid item>
                      <Button
                        component={Link}
                        href="#"
                        {...nextBtnProps}
                        className="toolbar__link noselect nextlink"
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleNextClick}
                      >
                        {t('buttons.next')}
                      </Button>
                    </Grid>
                  )}
                  {showSubmitBtn && (
                    <Grid item>
                      <Button
                        component={Link}
                        href="#"
                        {...submitBtnProps}
                        className="toolbar__link nextlink"
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitClick}
                      >
                        {t('buttons.seeResults')}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Hidden>
            )}
          </Grid>
          {showProfileBtn && isAuth && (
            <Guard iframe={false} partner={{ use_authorizing: true }}>
              <Grid item xs>
                <Tooltip title={t('common.menu.profile')}>
                  <RoundButton
                    className={clsx('round-button profile-button')}
                    color="primary"
                    variant="outlined"
                    aria-label="open drawer"
                    edge="start"
                    onClick={() => manageProfDrawerState(true)}
                  >
                    <PersonIcon fontSize="large" />
                  </RoundButton>
                </Tooltip>
              </Grid>
            </Guard>
          )}
        </Grid>
      </StyledToolbar>
    </StyledAppBar>
  );
};

HeaderWizard.defaultProps = {
  type: 'default', // default | results | fmb
  hideSteppers: false,
  questionStepperProps: {},
  channelStepperProps: {},
  showPrev: true,
  showNext: true,
  onPrevPress: () => {},
  onNextPress: () => {},
  prevBtnProps: {},
  nextBtnProps: {},
  showSubmitBtn: false,
  onSubmit: () => {},
  submitBtnProps: {},
  showProfileBtn: false,
  questionsCount: 0,
  channelQuestionsCount: 2,
  partner: {},
  loading: false,
  location: '',
};

HeaderWizard.propTypes = {
  type: PropTypes.string,
  hideSteppers: PropTypes.bool,
  questionStepperProps: PropTypes.object,
  channelStepperProps: PropTypes.object,
  showPrev: PropTypes.bool,
  showNext: PropTypes.bool,
  onPrevPress: PropTypes.func,
  onNextPress: PropTypes.func,
  prevBtnProps: PropTypes.object,
  nextBtnProps: PropTypes.object,
  showSubmitBtn: PropTypes.bool,
  onSubmit: PropTypes.func,
  submitBtnProps: PropTypes.object,
  showProfileBtn: PropTypes.bool,
  questionsCount: PropTypes.number,
  channelQuestionsCount: PropTypes.number,
  partner: PropTypes.object,
  pageName: PropTypes.string,
  loading: PropTypes.bool,
  location: PropTypes.string,
};

export default React.memo(HeaderWizard);
